import { Injectable } from '@angular/core';
import { MissingTranslationHandler, MissingTranslationHandlerParams } from '@ngx-translate/core';

/* eslint-disable max-len */
@Injectable({
  providedIn: 'root',
})
export class DefaultTranslationProvider implements MissingTranslationHandler {
  // We need default translations for UI elements that are shared between sites that don't have
  // translations yet or that are accessible by anonymous users.
  readonly defaultTranslations: {[key: string]: string} = {
    privacyLink: 'Privacy',
    legalLink: 'Legal',
    sysRequirementsLink: 'System Requirements',
    techSupportLink: 'Technical Support',
    helpLink: 'Help',
    myAccountLink: 'My Account',
    chooseLanguageLink: 'Choose Language',
    logoutLink: 'Logout',

    // Parent translations that are now also used in teacher translations.
    'parentScores-activity': 'Activity',
    'parentScores-assessment': 'Assessment',
    'parentScores-chooseProduct': 'Choose Product',
    'parentScores-chooseProductMessage': `Choose a product above to see this student's progress.`,
    'parentScores-comprehension': 'Comprehension',
    'parentScores-created': 'Created',
    'parentScores-date': 'Date',
    'parentScores-dateCompleted': 'Date Completed',
    'parentScores-decoding': 'Decoding',
    'parentScores-dueDate': 'Due Date',
    'parentScores-priRep': 'Print Scores',
    'parentScores-exRep': 'Export Scores',
    'parentScores-independentActivities': 'Independent Activities',
    'parentScores-indClassrep': 'back to Independent Work',
    'parentScores-individualReport': 'Individual Report',
    'parentScores-individualProgress': 'INDIVIDUAL PROGRESS REPORT',
    'parentScores-memoryWords': 'Memory Words',
    'parentScores-noScores': 'There are no scores to show because this student has not completed any assessments yet.',
    'parentScores-noWork': 'There is no work for this student.',
    'parentScores-noQuestWork': 'There are no quests for this student.',
    'parentScores-phonemicAwareness': 'Phonemic Awareness',
    'parentScores-phonics': 'Phonics',
    'parentScores-phonologicalAwareness': 'Phonological Awareness',
    'parentScores-product': 'Product',
    'parentScores-progressReport': 'Progress Report',
    'parentScores-popUptitle': 'ON-PAGE HELP',
    'parentScores-questName': 'Quest Name',
    'parentScores-quests': 'Quests',
    'parentScores-sprObj': 'Objective',
    'parentScores-sprPart': 'Part',
    'parentScores-sprCut': 'Cut-Off',
    'parentScores-scoreAndOnlineLink': 'Score',
    'parentScores-structuralAnalysis': 'Structural Analysis',
    'parentScores-time': 'Time',
    'parentScores-timeCompleted': 'Time Completed',
    'parentScores-type': 'Type',
    'parentScores-writing': 'Writing',

    // New translations below for Parent Portal Redesign
    'header-HLLogo': 'Highlights Portal logo return to Overview',
    'header-ZBLogo': 'ZB Portal logo return to Overview',
    'toast-savedLocale-success': 'Successfully saved your locale',
    'toast-savedLocale-error': 'Failed to update language preference.',
    'header-hasSubmenu': 'has submenu',
    'header-chooseRole': 'Choose Role',
    'header-noClassroom': 'No Classrooms',
    'parent-subnav-progressReportsLink': 'Progress Reports',
    'parent-subnav-classUpdatesLink': 'Class Updates',
    'parent-subnav-resourceRepositoryLink': 'Resource Repository',
    'parent-subnav-studentAssignmentsLink': 'Student Assignments',
    'parent-subnav-zbpForStudentsLink': 'ZB Portal For Students',
    'parent-subHeading-schoolYear': 'School Year',
    'parent-pageTitle-classUpdates': 'Class Updates',
    'parent-pageTitle-homePage': 'Parent Homepage',
    'parent-teacherMessage-title': 'Message From The Teacher:',
    'parent-pageWelcome': 'Welcome to MyZBPortal.com!',
    'parent-noStudents-section1': '<p>As a parent or guardian, you have access to scores from your child’s online activities, as well as assignments and messaging from your child’s teacher.</p>\n\n<p>There are currently no students associated with your account.</p>\n\n<p>You need at least one student associated with your account to be able to use MyZBPortal.com.<br>Please talk to your child’s teacher about adding them to your account.</p>',
    'parent-noClassrooms-section1': '<p>As a parent or guardian, you have access to scores from your child’s online activities, as well as assignments and messaging from your child’s teacher.</p>\n\n<p>Your child is currently not in a classroom.</p>\n\n<p>Your child needs to be in at least one classroom to be able to use MyZBPortal.com. Please talk to your  child’s teacher about adding your child to a classroom.</p>',
    'parent-noLicenses-section1': '<p>For this class, your child does not currently have a digital license assigned for this school year.</p>\n\n<p>If you believe you are receiving this message in error, please contact your child’s teacher.</p>',
    'parent-select-language': 'Please select your language.',
    'select-language': 'Select Language',
    'parent-login-as-student': `Successfully logged in as`,
    'zbpStudents-parentDashboard': `<p>Your child has been given access to digital resources in ZB Portal for Students. They can log into this area from {{ siteUrl }}.</p>\n\n<p>To log into {{ siteUrl }}, your child will need the following:\n<ul>\n<li><strong>Student's username</strong> - {{ username }}</li>\n<li><strong>Student's password</strong> - <em>provided by their school</em></li>\n<li><strong>Student's school ID</strong> - {{ schoolid }}</li>\n</ul>\n</p>\n\n<p>If your student does not know their password, please contact their teacher for assistance. Your child's teacher can reset their password.</p>\n\n<p>If your student needs access to ZB Portal and you are not able to contact their teacher, you may click the link below to log into ZB Portal for your child.</p>\n\n<p><em>Please note: logging in for your child will log you out of the Parent Portal.</em></p>`,
    'shared-materials': 'Materials',
    'shared-no-materials': 'There are no materials for this product.',
    'parent-resource-repository': 'Resource Repository',
    'parent-view-product': 'View Product',
    'parent-no-resources': 'There are no resources available for this product.',
    'parent-report-name-field': 'Report',
    'parent-report-class-field': 'Class',
    'parent-report-product-field': 'Product',
    'parent-report-student-field': 'Student',
    'parent-report-clear-product': 'Clear Product',
    'parent-report-all-products': 'All Products',
    'parent-report-apply': 'Apply',
    'shared-student-over-time-report-label': 'Student Over Time Report Table',
    'shared-student-over-time-report-column-test': 'Test',
    'shared-student-over-time-report-column-student-scores-and-status': 'Student Scores and Status',
    'shared-button-close': 'Close',
    'shared-button-save': 'Save',
    'shared-score-detail-header': 'SCORE DETAIL',
    'shared-score-detail-label-edit': 'edit score',
    'shared-score-detail-label-print': 'print score',
    'shared-score-detail-field-student': 'Student',
    'shared-score-detail-field-class': 'Class',
    'shared-score-detail-field-product': 'Product',
    'shared-score-detail-field-activity': 'Activity',
    'shared-score-detail-column-part': 'Part',
    'shared-score-detail-column-objective': 'Skill Description',
    'shared-score-detail-column-score': 'Score',
    'shared-score-detail-column-benchmark': 'Benchmark',
    'shared-score-detail-column-time-spent': 'Time Spent',
    'shared-score-detail-column-collapsible-label': 'additional details',
    'shared-score-detail-collapsible-column-prompt': 'Prompt',
    'shared-score-detail-collapsible-column-answer': 'Student Answer',
    'shared-score-detail-collapsible-column-choices': 'Answer Choices',
    'shared-score-detail-collapsible-column-correct': 'Correct',
    'shared-score-detail-collapsible-column-page': 'Page',
    'shared-score-detail-collapsible-column-time': 'Time Spent',
    'shared-score-detail-icon-correct-label': 'correct',
    'shared-score-detail-icon-incorrect-label': 'incorrect',
    'shared-benchmark-label-below': 'Below',
    'shared-benchmark-label-on-level': 'On Level',
    'shared-benchmark-label-above-level': 'Above Level',
    'shared-benchmark-label-n-a': 'N/A',
    'shared-assessment-status-label-not-started': 'NOT STARTED',
    'shared-assessment-status-label-add-score': '+ ADD SCORE',
    'shared-assessment-status-label-partially-scored': '+ PARTIALLY SCORED',
    'shared-assessment-status-label-in-progress': 'TEST IS CURRENTLY IN PROGRESS',
    'shared-assessment-status-label-scoring-in-progress': 'SCORING IN PROGRESS',
    'shared-assessment-status-label-score-not-available': 'SCORE NOT AVAILABLE',
  };

  handle(params: MissingTranslationHandlerParams): string {
    return this.defaultTranslations[params.key] || params.key;
  }
}

/* eslint-enable max-len */
